/**
 * E60 — единый источник объяснения безопасной сделки.
 *
 * Правило: **SecureDealExplainer — единственный источник объяснения безопасной
 * сделки**, а его тексты живут здесь. Любое место, где продукт рассказывает
 * пользователю про заморозку денег, берёт строку отсюда — тогда расхождение
 * становится физически невозможным.
 *
 * Почему это понадобилось: мест оказалось четыре (объяснялка в модалке брони,
 * FAQ, карточка в хабе сессии, плашка статуса денег), и они разъехались. Часть
 * из них называла держателя замороженных денег — по оферте
 * (`secure-deal-offer.txt`) резервирование выполняет Банк, поэтому «у кого»
 * деньги лежат продукт не говорит вовсе.
 *
 * Второй инвариант — срок. Окно самостоятельного обращения = 48 часов
 * (`COMPLAINT_DEADLINE_HOURS`), и оно закреплено тестом
 * `src/__tests__/legal-copy-invariants.spec.ts`. Ручного «подтвердить занятие»
 * в продукте нет: сессия закрывается автоматически, молчание = спор не открыт,
 * — обещать подтверждение нельзя (П-10).
 */

export interface SecureDealStep {
    /** Выделенное начало пункта. */
    lead: string;
    /** Продолжение пункта обычным начертанием. */
    rest: string;
}

/** Шаги для развёрнутой объяснялки (SecureDealExplainer). */
export const SECURE_DEAL_STEPS: SecureDealStep[] = [
    {
        lead: 'Деньги замораживаются',
        rest: 'в момент оплаты — наставник их не получает.',
    },
    {
        lead: 'После занятия',
        rest: 'деньги остаются замороженными ещё 48 часов — на случай, если что-то пошло не так.',
    },
    {
        lead: 'Если что-то пошло не так',
        rest: '— откройте спор в течение 48 часов, и мы разберёмся. Если обращений нет, деньги уходят наставнику автоматически.',
    },
];

/** Ответ на FAQ-вопрос «Безопасна ли оплата?». */
export const SECURE_DEAL_FAQ_ANSWER =
    'Да, мы работаем по безопасной сделке. Деньги замораживаются в момент оплаты — ' +
    'наставник их не получает. После занятия они остаются замороженными ещё 48 часов: ' +
    'если что-то пошло не так, откройте спор в течение этого срока, и мы разберёмся. ' +
    'Если обращений нет, деньги уходят наставнику автоматически.';

/** Однострочное напоминание на карточке сделки (хаб сессии). */
export const SECURE_DEAL_SHORT =
    'Безопасная сделка: деньги заморожены и поступят наставнику после сессии — ' +
    'если за 48 часов вы не сообщите о проблеме.';

/** Подпись плашки статуса денег, пока оплата заморожена. */
export const SECURE_DEAL_ESCROW_PILL = '💰 Деньги под защитой сделки до сессии';

/**
 * Подпись под CTA там, где оплата ещё только предстоит (карточка записи в hero
 * профиля наставника, шаг подтверждения брони).
 *
 * Пятое место, где продукт говорит про безопасную сделку, и единственное, где
 * он обращается к ещё не заплатившему человеку. Формулировка — решение
 * владельца: под кнопкой называем выгоду и механику глазами пользователя
 * («наставник получит деньги только после сессии»), а не механику системы
 * («деньги замораживаются… и ещё на 48 часов»).
 *
 * Срок здесь намеренно не назван: развёрнутое объяснение с окном 48 часов
 * остаётся в SECURE_DEAL_STEPS, SECURE_DEAL_FAQ_ANSWER и SECURE_DEAL_SHORT.
 * Инвариант копии («если срок назван — то только 48 часов»,
 * src/__tests__/legal-copy-invariants.spec.ts) на строку без числа не
 * распространяется, а вот дрейф срока в тех трёх текстах он по-прежнему ловит.
 */
export const SECURE_DEAL_BOOKING_NOTE =
    'Наставник получит деньги только после сессии. Если она не состоится — вернём оплату.';
